import { createContext, useContext, useEffect, useState, useRef } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import { setUserContext, clearUserContext } from "@/lib/sentry";
import { clearVisitMemory } from "@/lib/queryClient";
import { logAuthEvent } from "@/services/authLogger";
import { prefetchFirstFeedPage } from "@/services/feedPrefetch";
import { loadStartQuality } from "@/services/playback/playbackQuality";
import { loadPlayerProgress } from "@/services/playback/playerProgress";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const hasLoggedOAuthRef = useRef(false);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        
        // Log OAuth login (connexion via Google/Facebook)
        if (event === 'SIGNED_IN' && session?.user && !hasLoggedOAuthRef.current) {
          const provider = session.user.app_metadata?.provider;
          if (provider && provider !== 'email') {
            hasLoggedOAuthRef.current = true;
            await logAuthEvent({
              action: 'oauth_login',
              userId: session.user.id,
              email: session.user.email,
              fullName: session.user.user_metadata?.full_name,
              metadata: { provider }
            });
          }
        }
        
        // Reset le flag quand l'utilisateur se déconnecte
        if (event === 'SIGNED_OUT') {
          hasLoggedOAuthRef.current = false;
        }
        
        // Mettre à jour le contexte Sentry
        if (session?.user) {
          setUserContext(session.user.id);
        } else {
          clearUserContext();
        }
        
        // Palier HLS global lu avant d'ouvrir le fil (lecture unique partagée).
        await loadStartQuality();
        setLoading(false);
      }
    );

    supabase.auth.getSession().then(async ({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      
      // Mettre à jour le contexte Sentry
      if (session?.user) {
        setUserContext(session.user.id);
        // Niveau 0 : pendant que les routes protégées se montent, on prépare
        // déjà la première page du feed (liste + jetons des 3 premières vidéos).
        prefetchFirstFeedPage();
        // Réglages CSS de la barre de progression (best-effort également).
        loadPlayerProgress();
      }
      
      // Le premier lecteur doit partir sur le palier choisi en administration.
      await loadStartQuality();
      setLoading(false);
    });


    return () => subscription.unsubscribe();
  }, []);

  const signOut = async () => {
    // Log déconnexion avant de se déconnecter
    if (user) {
      await logAuthEvent({
        action: 'logout',
        userId: user.id,
        email: user.email,
        fullName: user.user_metadata?.full_name
      });
    }
    
    await supabase.auth.signOut();
    clearVisitMemory(); // Le compte suivant ne voit rien du précédent
    clearUserContext(); // Nettoyer le contexte Sentry
    navigate("/auth");
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
